import axios from 'axios';

// Create Axios instance with base URL
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '/api',
  timeout: 60000, // Reduced to 60 seconds for better mobile compatibility
  headers: {
    'Content-Type': 'application/json'
  }
});

// CSRF token caching with session-specific keys
const tokenCache = new Map();
let activeTokenPromise = null;

// Track retry attempts per session to prevent infinite loops
const retryAttempts = new Map();
const MAX_RETRIES_PER_SESSION = 3;

// Response interceptor to clear retry counts on successful requests
api.interceptors.response.use(
  (response) => {
    // Clear retry count for this session on successful response
    if (response.config.headers.Authorization) {
      try {
        const token = response.config.headers.Authorization.substring(7);
        const sessionKey = token.substring(0, 20);
        retryAttempts.delete(sessionKey);
      } catch (e) {
        // Ignore extraction errors
      }
    }
    return response;
  },
  (error) => {
    // Let the existing error interceptor handle errors
    return Promise.reject(error);
  }
);

// Function to get CSRF token with session-aware caching
const getCSRFToken = async (authHeader) => {
  // Extract session identifier from auth header for cache key
  let sessionKey = 'anonymous';
  if (authHeader && authHeader.startsWith('Bearer ')) {
    try {
      const token = authHeader.substring(7);
      // Use the first 20 characters of the JWT as session key for caching
      sessionKey = token.substring(0, 20);
    } catch (error) {
      console.log('[CSRF] Could not extract session key from auth header');
    }
  }

  // If we already have a pending request for this session, wait for it
  if (activeTokenPromise) {
    try {
      await activeTokenPromise;
    } catch (error) {
      // If the pending request failed, we'll try again
      activeTokenPromise = null;
    }
  }

  // Check if we have a cached token for this session
  const cachedEntry = tokenCache.get(sessionKey);
  if (cachedEntry && Date.now() - cachedEntry.timestamp < 20 * 60 * 1000) { // 20 minutes
    return cachedEntry.token;
  }

  // Create a new token request
  activeTokenPromise = (async () => {
    try {
      const csrfResponse = await axios.get('/api/csrf-token', {
        headers: {
          'Authorization': authHeader || ''
        }
      });
      
      if (csrfResponse.data && csrfResponse.data.token) {
        const newToken = csrfResponse.data.token;
        
        // Cache token with timestamp for this specific session
        tokenCache.set(sessionKey, {
          token: newToken,
          timestamp: Date.now()
        });
        
        return newToken;
      } else {
        throw new Error('No token in response');
      }
    } catch (error) {
      console.warn('[CSRF] Token fetch failed:', error.message);
      throw error;
    } finally {
      activeTokenPromise = null;
    }
  })();

  return await activeTokenPromise;
};

// Add request interceptor for auth and CSRF
api.interceptors.request.use(
  async (config) => {
    // Get auth token from sessionStorage for authenticated requests
    try {
      const authStr = sessionStorage.getItem('auth_token');
      if (authStr) {
        const auth = JSON.parse(authStr);
        if (auth && auth.token) {
          // Set auth header for all requests
          config.headers.Authorization = `Bearer ${auth.token}`;
        }
      }
    } catch (e) {
      console.warn('SessionStorage access failed on mobile device:', e.message);
      // Continue without auth token - request may fail but won't crash
    }

    // Add CSRF token for state-changing operations (but not for the CSRF token request itself)
    if (['POST', 'PUT', 'DELETE', 'PATCH'].includes(config.method?.toUpperCase()) && 
        !config.url?.includes('/csrf-token')) {
      try {
        const token = await getCSRFToken(config.headers.Authorization);
        config.headers['X-CSRF-Token'] = token;
      } catch (csrfError) {
        console.warn('Failed to get CSRF token:', csrfError);
        // Continue without CSRF token - request may fail but won't crash
      }
    }
    
    // Debug logs for network troubleshooting
    if (config.data && (config.url?.includes('/login') || config.url?.includes('/register'))) {
      // Only log email for security, never log passwords
      const data = JSON.parse(JSON.stringify(config.data));
      if (data.password) data.password = '[REDACTED]';
      console.log('Request data:', data);
    }
    
    return config;
  },
  (error) => {
    console.error('Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Add response interceptor to handle errors
api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    // Check if this is the expected compound-defense/status error or business supply generation
    const url = error.config?.url || '';
    const status = error.response?.status;
    const isCompoundDefenseStatus = url.includes('compound-defense/status');
    const isSupplyGeneration = url.includes('/business/generate-supplies');
    const isExpectedError = ((status === 404 || status === 401) && isCompoundDefenseStatus) || 
                           (status === 400 && isSupplyGeneration);
    
    // Handle authentication errors for ATM movement and other operations
    const isATMMovement = url.includes('/move-computer-near-atm');
    const isAuthError = error.response?.data?.code === 'CSRF_AUTH_REQUIRED' || status === 401;
    
    // Handle CSRF token errors with automatic retry
    // Also retry 403 errors on animation deletion as they're likely CSRF-related
    const isAnimationDeletion = url.includes('/drone/animation/remove/');
    const isCsrfError = error.response?.data?.code === 'CSRF_TOKEN_INVALID' || error.response?.data?.message?.includes('CSRF');
    
    // Special handling for ATM movement authentication errors
    if (isATMMovement && isAuthError) {
      console.error('ATM Movement Authentication Error:', error.response?.data?.message || 'Authentication failed');
      const customError = new Error('Authentication expired. Please refresh the page and try again.');
      customError.response = error.response;
      customError.isAuthError = true;
      return Promise.reject(customError);
    }
    
    if (status === 403 && (isCsrfError || isAnimationDeletion)) {
      if (isAnimationDeletion) {
        console.log('[CSRF] Animation deletion 403 error, clearing session cache for retry...');
      } else {
        console.log('[CSRF] Token invalid, clearing session cache for retry...');
      }
      
      // Clear the cache for this specific session
      const authHeader = error.config.headers.Authorization;
      let sessionKey = 'default';
      if (authHeader && authHeader.startsWith('Bearer ')) {
        try {
          const token = authHeader.substring(7);
          sessionKey = token.substring(0, 20);
          tokenCache.delete(sessionKey);
          console.log('[CSRF] Cleared cache for session:', sessionKey.substring(0, 8) + '...');
        } catch (e) {
          console.log('[CSRF] Could not determine session key for cache clearing');
        }
      }
      
      // Check session-based retry limit to prevent infinite loops
      const currentRetries = retryAttempts.get(sessionKey) || 0;
      if (currentRetries >= MAX_RETRIES_PER_SESSION) {
        console.error(`[CSRF] Session retry limit exceeded (${MAX_RETRIES_PER_SESSION}), request failed permanently`);
        retryAttempts.delete(sessionKey); // Reset for next batch
        return Promise.reject(error);
      }
      
      // Only retry once per request to avoid infinite loops
      if (!error.config._retryCount) {
        error.config._retryCount = 1;
        retryAttempts.set(sessionKey, currentRetries + 1);
        console.log(`[CSRF] Retrying request with fresh token (attempt ${currentRetries + 1}/${MAX_RETRIES_PER_SESSION})...`);
        
        try {
          // Force fetch new token and retry the original request
          const newToken = await getCSRFToken(error.config.headers.Authorization);
          error.config.headers['X-CSRF-Token'] = newToken;
          console.log('[CSRF] Retrying with new token:', newToken.substring(0, 8) + '...');
          
          return api.request(error.config);
        } catch (retryError) {
          console.error('[CSRF] Retry failed:', retryError);
          return Promise.reject(retryError);
        }
      } else {
        console.error('[CSRF] Single request retry limit exceeded, request failed permanently');
      }
    }
    
    // Debug logging to understand what's happening (only in development)
    if (process.env.NODE_ENV === 'development') {
      if (isCompoundDefenseStatus) {
        console.log(`Compound defense status request: ${url} returned ${status} - ${isExpectedError ? 'suppressed' : 'logged'}`);
      }
      
      if (!isExpectedError) {
        console.error(`Error response from ${url || 'unknown endpoint'}:`, 
          status || 'No status', 
          error.message || 'Unknown error');
      }
    }
    
    // Handle authentication errors (but skip expected compound-defense 401s)
    if (error.response && error.response.status === 401 && !error.config?.url?.includes('/compound-defense/status')) {
      console.log('Authentication failed - user needs to login');
      
      // Clear stored auth data
      localStorage.clear(); // Clear all localStorage
      sessionStorage.clear();
    }
    
    // Always pass through errors - no mock data fallbacks
    return Promise.reject(error);
  }
);

// AUTH API
export const login = async (email, password) => {
  try {
    console.log('Attempting login with email:', email);
    
    // Use provided credentials with no special handling
    const response = await api.post('/auth/login', { 
      email, 
      password 
    });
    
    console.log('Login response received:', response.status);
    return response.data;
  } catch (error) {
    console.error('Login error details:', error);
    // Add better error logging
    if (error.response) {
      console.error('Response status:', error.response.status);
      console.error('Response data:', error.response.data);
    } else if (error.request) {
      console.error('No response received:', error.request);
    }
    throw error;
  }
};

export const register = async (userData) => {
  try {
    const { username, email, password } = userData;
    
    // Use provided credentials with no special handling
    console.log(`Registration request to ${api.defaults.baseURL}/auth/register`);
    
    const response = await api.post('/auth/register', { 
      username, 
      email, 
      password 
    });
    
    console.log('Registration response received:', response.status);
    return response.data;
  } catch (error) {
    console.error('Registration error details:', error);
    // Add better error logging
    if (error.response) {
      console.error('Response status:', error.response.status);
      console.error('Response data:', error.response.data);
    } else if (error.request) {
      console.error('No response received:', error.request);
    }
    throw error;
  }
};

export const getUserProfile = async () => {
  const response = await api.get('/auth/me');
  return response.data;
};

// TASKS API
export const getAllTasks = async () => {
  const response = await api.get('/tasks');
  return response.data;
};

export const getUserTaskProgression = async () => {
  const response = await api.get('/tasks/user/progression');
  return response.data;
};

export const getUserTasks = async (completed = false, type = null) => {
  let url = `/tasks/user/tasks?completed=${completed}`;
  if (type) {
    url += `&type=${type}`;
  }
  const response = await api.get(url);
  return response.data;
};

export const getTasksByType = async (type) => {
  const response = await api.get(`/tasks/type/${type}`);
  return response.data;
};

export const getTaskById = async (id) => {
  const response = await api.get(`/tasks/task/${id}`);
  return response.data;
};

export const submitTaskSolution = async (taskId, solution) => {
  const response = await api.post(`/tasks/submit/${taskId}`, { solution });
  const result = response.data;
  
  // Task rewards will be handled by AuthContext sync, not localStorage
  if (result && result.success && result.rewards) {
    console.log(`Task completed: +${result.rewards.xp} XP, +${result.rewards.wrCoins} WR Coins`);
  }
  
  return result;
};

export const generateTask = async (type, difficulty) => {
  console.log(`Generating new ${difficulty} ${type} task...`);
  
  try {
    let response;
    
    // Special case for network tasks - use the dedicated route
    if (type === 'network') {
      console.log('Using special network task generation route');
      response = await api.post(`/tasks/generate/network/${difficulty}`);
    } else {
      // Use the main task generation endpoint for other task types
      response = await api.post('/tasks/generate', { 
        type, 
        difficulty 
      });
    }
    
    if (response && response.data && response.data.task) {
      console.log('Task successfully generated from server:', response.data.task);
      return response.data.task;
    } else if (response && response.data && response.data._id) {
      // Response is already a task object
      console.log('Direct task object received from server:', response.data);
      return response.data;
    } else {
      console.error('Unexpected response format:', response.data);
      throw new Error('The server returned an unexpected response format for the task generation request');
    }
  } catch (error) {
    console.error('Error generating task:', error);
    
    // Provide detailed error information for better UX
    let errorMessage = 'Task generation failed. Please try again later.';
    
    if (error.response) {
      // Server responded with an error
      const status = error.response.status;
      const serverMessage = error.response.data?.message || 'Unknown server error';
      
      if (status === 503) {
        errorMessage = `AI service is currently unavailable. Please try again in a few minutes.`;
      } else if (status === 400) {
        if (serverMessage.includes('Website task must contain a valid URL')) {
          errorMessage = `Website task generation failed: URL validation error. The AI generated a task without a proper URL.`;
        } else if (serverMessage.includes('Website task must use a URL from an allowed domain')) {
          errorMessage = `Website task generation failed: The URL domain is not in the allowed list. Only use websites from popular educational sites.`;
        } else if (serverMessage.includes('Website task cannot be created with disallowed domain')) {
          errorMessage = `Website task generation failed: The requested website domain is not allowed. Please request a task for an educational or popular website.`;
        } else if (serverMessage.includes('Website task contains malformed URL')) {
          errorMessage = `Website task generation failed: The task contains an invalid URL. Please try again with a valid website.`;
        } else if (serverMessage.includes('Website task must contain a valid URL with http')) {
          errorMessage = `Website task generation failed: The URL must start with http:// or https://. Please try again.`;
        } else {
          errorMessage = `Invalid request: ${serverMessage}`;
        }
      } else if (status === 429) {
        errorMessage = `Too many requests. Please wait a moment before generating more tasks.`;
      } else {
        errorMessage = `Task generation failed (${status}): ${serverMessage}`;
      }
      
      // Log additional details for debugging website tasks
      if (type === 'website' && error.response.data) {
        console.error('Website task validation details:', error.response.data);
      }
    } else if (error.request) {
      // No response received from server
      errorMessage = `Network error: Could not reach the server. Please check your connection.`;
    } else if (error.message) {
      // Error in setting up the request
      errorMessage = `Task generation failed: ${error.message}`;
    }
    
    throw new Error(errorMessage);
  }
};

// Local task generator function for fallbacks
const generateLocalTask = (type, difficulty) => {
  console.log(`Generating local fallback task for ${type} ${difficulty}`);
  
  const taskId = `task-local-${Date.now()}`;
  let title, description, content, solution, hint;
  
  // Network task templates
  if (type === 'network') {
    if (difficulty === 'easy') {
      title = 'Port Scanner';
      description = 'Identify which commonly used ports are associated with these network services.';
      content = 'Match the following services to their standard ports: SSH, HTTP, HTTPS, FTP, DNS';
      solution = 'SSH: 22, HTTP: 80, HTTPS: 443, FTP: 21, DNS: 53';
      hint = 'These are the most common default ports for internet services.';
    } else if (difficulty === 'medium') {
      title = 'Subnet Calculator';
      description = 'Calculate the subnet information for this network.';
      content = 'For the IP address 192.168.1.100 with subnet mask 255.255.255.0, determine: Network address, Broadcast address, Number of usable hosts';
      solution = 'Network: 192.168.1.0, Broadcast: 192.168.1.255, Usable hosts: 254';
      hint = 'The subnet mask 255.255.255.0 is equivalent to /24 in CIDR notation.';
    } else if (difficulty === 'hard') {
      title = 'Packet Analysis';
      description = 'Analyze this network packet capture and identify the attack.';
      content = 'Examine this TCP packet sequence:\nSYN from 10.0.0.1 to 10.0.0.2\nSYN from 10.0.0.1 to 10.0.0.2\nSYN from 10.0.0.1 to 10.0.0.2\nSYN from 10.0.0.1 to 10.0.0.2\nSYN from 10.0.0.1 to 10.0.0.2\n[1000 more SYN packets]\nNo SYN-ACK responses observed.';
      solution = 'SYN Flood DDoS Attack';
      hint = 'This attack exploits the three-way handshake of TCP connections.';
    } else { // expert
      title = 'Advanced Firewall Configuration';
      description = 'Design a firewall rule to mitigate a sophisticated attack pattern.';
      content = 'Your network is experiencing a complex attack where attackers are sending malformed packets that pass standard filtering but cause services to crash. These packets have the following characteristics:\n1. TCP packets with SYN-ACK flags set together with FIN\n2. Destination port alternates between 80 and 443\n3. Payload contains the hex sequence 0xDEADBEEF\n4. TTL value is exactly 128\n\nWrite an iptables rule to block these specific packets.';
      solution = 'iptables -A INPUT -p tcp --dport 80,443 --tcp-flags SYN,ACK,FIN SYN,ACK,FIN -m string --hex-string "|DEADBEEF|" --algo bm -m ttl --ttl-eq 128 -j DROP';
      hint = 'You need to combine multiple match conditions including tcp flags, string matching, and TTL values.';
    }
  } else {
    // Generic fallback for other types
    title = `${type.charAt(0).toUpperCase() + type.slice(1)} Challenge ${Math.floor(Math.random() * 1000)}`;
    description = `A ${difficulty} level ${type} challenge created locally.`;
    content = `This is a locally generated ${type} task. The actual content would contain a problem to solve.`;
    solution = `This is a sample solution for the ${type} task.`;
    hint = `Here's a hint that would help with the ${type} challenge.`;
  }
  
  // Calculate rewards based on difficulty
  const xpReward = difficulty === 'easy' ? 25 : difficulty === 'medium' ? 45 : difficulty === 'hard' ? 70 : 100;
  const coinsReward = difficulty === 'easy' ? 10 : difficulty === 'medium' ? 20 : difficulty === 'hard' ? 30 : 50;
  
  return {
    _id: taskId,
    title,
    description,
    type,
    difficulty,
    xpReward,
    coinsReward,
    reward: xpReward, // For backward compatibility
    content,
    solution,
    hint,
    createdAt: new Date(),
    updatedAt: new Date()
  };
};

export const deleteTask = async (taskId) => {
  try {
    // Check if this is a locally generated task ID (starts with task-generated or task-local)
    if (taskId.startsWith('task-generated-') || taskId.startsWith('task-local-')) {
      console.log(`Deleting local task: ${taskId} (skipping server request)`);
      return { success: true, message: 'Local task removed' };
    }
    
    // For database tasks, make an actual DELETE request to the server
    console.log(`Deleting server task: ${taskId}`);
    const response = await api.delete(`/tasks/task/${taskId}`);
    return response.data;
  } catch (err) {
    console.error('Error deleting task from server:', err);
    // Even if the server request fails, return success to allow the client to remove it locally
    return { success: true, message: 'Task removed from local storage' };
  }
};

// COMPUTER PARTS API
export const getComputerParts = async () => {
  const response = await api.get('/computer/parts');
  return response.data;
};

// Alias for backward compatibility
export const getParts = getComputerParts;

export const buyComputerPart = async (partId) => {
  const response = await api.post(`/computer/parts/buy/${partId}`);
  return response.data;
};

// Alias for backward compatibility
export const buyPart = buyComputerPart;

// MINING API
export const getMiningStatus = async () => {
  const response = await api.get('/computer/mining');
  return response.data;
};

export const collectMiningRewards = async (pendingRewards, lastCollection) => {
  const response = await api.post('/computer/mining/collect', { 
    pendingRewards, 
    lastCollection 
  });
  return response.data;
};

// Alias for backward compatibility
export const collectMiningCoins = collectMiningRewards;

export const rebootMiningRig = async () => {
  const response = await api.post('/computer/mining/reboot');
  return response.data;
};

// CHAT API
export const sendChatMessage = async (message, taskContext = null) => {
  const response = await api.post('/chat', { message, taskContext });
  return response.data;
};

// AI ASSISTANT API
export const getAIAssistantResponse = async (message, taskType, userId = 'guest', chatHistory = []) => {
  console.log(`Getting AI assistant response for task type: ${taskType}, userId: ${userId}`);
  
  // Format chat history for the API
  const formattedHistory = chatHistory.map(msg => ({
    role: msg.isUser ? 'user' : 'assistant',
    content: msg.content
  }));
  
  const requestData = {
    message,
    taskType,
    userId,
    chatHistory: formattedHistory
  };

  const response = await api.post('/assistant/chat', requestData);
  return response.data;
};

export const clearAIAssistantHistory = async (userId = 'guest') => {
  const response = await api.post('/assistant/clear', { userId });
  return response.data;
};

// CHAT CONVERSATIONS API
export const getUserConversations = async () => {
  const response = await api.get('/chat/conversations');
  return response.data;
};

export const createConversation = async (name) => {
  const response = await api.post('/chat/conversations', { name });
  return response.data;
};

export const getConversation = async (id) => {
  const response = await api.get(`/chat/conversations/${id}`);
  return response.data;
};

export const updateConversationName = async (id, name) => {
  const response = await api.put(`/chat/conversations/${id}`, { name });
  return response.data;
};

export const deleteConversation = async (id) => {
  const response = await api.delete(`/chat/conversations/${id}`);
  return response.data;
};

export const addMessageToConversation = async (conversationId, message, taskType) => {
  const response = await api.post(`/chat/conversations/${conversationId}/messages`, {
    message,
    taskType
  });
  return response.data;
};

export const clearConversationMessages = async (conversationId) => {
  const response = await api.delete(`/chat/conversations/${conversationId}/messages`);
  return response.data;
};

// PROFILE API
export const syncUserData = async (data) => {
  const response = await api.post('/profile/sync', { data });
  return response.data;
};

// Helper for forcing fetch of fresh data
export const apiForceRefresh = () => {
  // Force refresh of API data - implementation could include cache clearing
  return true;
};

// COMMUNITY TASKS API
export const getAllCommunityTasks = async () => {
  const response = await api.get('/community-tasks');
  return response.data;
};

export const getCommunityTaskById = async (id) => {
  const response = await api.get(`/community-tasks/task/${id}`);
  return response.data;
};

export const createCommunityTask = async (taskData) => {
  const response = await api.post('/community-tasks/create', taskData);
  return response.data;
};

export const updateCommunityTask = async (id, taskData) => {
  const response = await api.put(`/community-tasks/task/${id}`, taskData);
  return response.data;
};

export const deleteCommunityTask = async (id) => {
  const response = await api.delete(`/community-tasks/task/${id}`);
  return response.data;
};

export const submitCommunityTaskSolution = async (id, solution) => {
  const response = await api.post(`/community-tasks/submit/${id}`, { solution });
  return response.data;
};

export const payForCommunityTaskAttempt = async (id) => {
  const response = await api.post(`/community-tasks/pay/${id}`);
  return response.data;
};

export const rateCommunityTask = async (id, rating) => {
  const response = await api.post(`/community-tasks/rate/${id}`, { rating });
  return response.data;
};

export const getUserCreatedTasks = async () => {
  const response = await api.get('/community-tasks/user/created');
  return response.data;
};

export const getCommunityTasksByType = async (type) => {
  const response = await api.get(`/community-tasks/type/${type}`);
  return response.data;
};

// Report community task
export const reportCommunityTask = async (id, reason, description) => {
  const response = await api.post(`/community-tasks/report/${id}`, { reason, description });
  return response.data;
};

// Admin functions for community tasks
export const getReportedTasks = async () => {
  const response = await api.get('/community-tasks/admin/reported');
  return response.data;
};

export const adminDeleteTask = async (id) => {
  const response = await api.delete(`/community-tasks/admin/task/${id}`);
  return response.data;
};

export const adminRestoreTask = async (id) => {
  const response = await api.patch(`/community-tasks/admin/restore/${id}`);
  return response.data;
};

export const getCommunityPoolStats = async () => {
  const response = await api.get('/community-tasks/admin/pool-stats');
  return response.data;
};

// Occult Bank API functions
export const getOccultBankStats = async () => {
  const response = await api.get('/occult-balance/status');
  return response.data;
};

export const forceOccultDistribution = async () => {
  const response = await api.post('/occult-balance/distribute');
  return response.data;
};

// Admin user management functions
export const getAllUsers = async () => {
  console.log('Making request to get all users');
  try {
    const response = await api.get('/admin/users');
    console.log('Get all users response:', response.status, response.data ? `Found ${response.data.length} users` : 'No data');
    return response.data || [];
  } catch (error) {
    console.error('Error in getAllUsers:', error.message);
    if (error.response) {
      console.error('Response status:', error.response.status);
      console.error('Response data:', error.response.data);
    }
    throw error;
  }
};

export const makeUserAdmin = async (userId) => {
  const response = await api.post(`/admin/users/${userId}/make-admin`);
  return response.data;
};

export const revokeAdminRole = async (userId) => {
  const response = await api.post(`/admin/users/${userId}/revoke-admin`);
  return response.data;
};

export const updateUserCoins = async (userId, wrCoins) => {
  const response = await api.put(`/admin/users/${userId}/coins`, { wrCoins });
  return response.data;
};

export const updateUserXP = async (userId, xp) => {
  const response = await api.put(`/admin/users/${userId}/xp`, { xp });
  return response.data;
};

export const changeUserPassword = async (userId, newPassword) => {
  const response = await api.put(`/admin/users/${userId}/password`, { newPassword });
  return response.data;
};

export const deleteUser = async (userId) => {
  const response = await api.delete(`/admin/users/${userId}`);
  return response.data;
};

export const runCleanupJob = async () => {
  const response = await api.post('/admin/run-cleanup');
  return response.data;
};

// Featured tasks API functions
export const getFeaturedTasks = async () => {
  const response = await api.get('/community-tasks/featured');
  return response.data;
};

export const searchCommunityTasksForAdmin = async (username, taskTitle, limit = 50, unifiedQuery = null) => {
  const params = new URLSearchParams();
  if (unifiedQuery) {
    // Use unified search when query is provided
    params.append('query', unifiedQuery);
  } else {
    // Use legacy separate search parameters
    if (username) params.append('username', username);
    if (taskTitle) params.append('taskTitle', taskTitle);
  }
  if (limit) params.append('limit', limit);
  
  const response = await api.get(`/community-tasks/admin/search?${params.toString()}`);
  return response.data;
};

export const setFeaturedTasks = async (taskIds) => {
  const response = await api.post('/community-tasks/admin/featured', { taskIds });
  return response.data;
};

// Enhanced public search for community tasks with unified and legacy support
export const searchCommunityTasks = async (username, taskTitle, limit = 50, unifiedQuery = null) => {
  const params = new URLSearchParams();
  if (unifiedQuery) {
    // Use unified search when query is provided
    params.append('query', unifiedQuery);
  } else {
    // Use legacy separate search parameters for backwards compatibility
    if (username) params.append('username', username);
    if (taskTitle) params.append('taskTitle', taskTitle);
  }
  if (limit) params.append('limit', limit);
  
  const response = await api.get(`/community-tasks/search?${params.toString()}`);
  return response.data;
};

// Convenience function for unified search only
export const unifiedSearchCommunityTasks = async (query, limit = 50) => {
  const params = new URLSearchParams();
  params.append('query', query);
  if (limit) params.append('limit', limit);
  
  const response = await api.get(`/community-tasks/search?${params.toString()}`);
  return response.data;
};

// Get community tasks within radius
export const getCommunityTasksInRadius = async (lat, lng, radius) => {
  const params = new URLSearchParams();
  params.append('lat', lat);
  params.append('lng', lng);
  params.append('radius', radius);
  
  const response = await api.get(`/community-tasks/in-radius?${params.toString()}`);
  return response.data;
};

// Get task details for interaction
export const getCommunityTaskForInteraction = async (taskId) => {
  const response = await api.get(`/community-tasks/interaction/${taskId}`);
  return response.data;
};

// Submit solution for location-based task
export const submitLocationTaskSolution = async (taskId, solution) => {
  const response = await api.post('/community-tasks/submit-location', {
    taskId,
    solution
  });
  return response.data;
};

// Place community task on map
export const placeTaskOnMap = async (taskId, lat, lng) => {
  const response = await api.post('/community-tasks/place-on-map', {
    taskId,
    lat,
    lng
  });
  return response.data;
};

// Move community task on map (weekly limit)
export const moveTaskOnMap = async (taskId, lat, lng) => {
  const response = await api.post('/community-tasks/move-on-map', {
    taskId,
    lat,
    lng
  });
  return response.data;
};

// Get user's placed tasks
export const getUserPlacedTasks = async () => {
  const response = await api.get('/community-tasks/user/placed');
  return response.data;
};

// Validate task placement location
export const validateTaskPlacement = async (lat, lng, excludeTaskId = null) => {
  const params = new URLSearchParams();
  params.append('lat', lat);
  params.append('lng', lng);
  if (excludeTaskId) params.append('excludeTaskId', excludeTaskId);
  
  const response = await api.get(`/community-tasks/validate-placement?${params.toString()}`);
  return response.data;
};

// Field agent remove community task from map
export const fieldAgentRemoveTask = async (taskId, reason) => {
  const response = await api.post('/community-tasks/field-agent-remove', {
    taskId,
    reason
  });
  return response.data;
};

// Top Hacker Leaderboard API
export const getTopHackers = async (limit = 200) => {
  const params = new URLSearchParams();
  if (limit) params.append('limit', limit);
  
  // Use shorter timeout for leaderboard requests
  const response = await api.get(`/leaderboard/top-hackers?${params.toString()}`, {
    timeout: 30000 // 30 seconds timeout for leaderboard
  });
  return response.data;
};

// Top Task Players Leaderboard API
export const getTopTaskPlayers = async (limit = 200) => {
  const params = new URLSearchParams();
  if (limit) params.append('limit', limit);
  
  // Use shorter timeout for leaderboard requests
  const response = await api.get(`/leaderboard/top-task-players?${params.toString()}`, {
    timeout: 30000 // 30 seconds timeout for leaderboard
  });
  return response.data;
};

// Enhanced Business Supply Chain API
export const purchaseFromSupplyChain = async (sellerBusinessId, buyerBusinessId, quantity) => {
  const response = await api.post('/business/purchase-from-supply-chain', {
    sellerBusinessId,
    buyerBusinessId,
    quantity
  });
  return response.data;
};

export const getAvailableSuppliers = async (businessId) => {
  const response = await api.get(`/business/suppliers/${businessId}`);
  return response.data;
};

export const updateBusinessPricing = async (businessId, businessSellPrice, userSellPrice) => {
  const response = await api.post('/business/update-business-pricing', {
    businessId,
    businessSellPrice,
    userSellPrice
  });
  return response.data;
};

export const acceptUpstreamPricing = async (businessId, upstreamSupplierId) => {
  const response = await api.post('/business/accept-upstream-pricing', {
    businessId,
    upstreamSupplierId
  });
  return response.data;
};

// SWAT System API
export const getRecentAttackers = async () => {
  const response = await api.get('/swat/recent-attackers');
  return response.data;
};

export const removeAttacker = async (attackerId) => {
  const response = await api.post('/swat/remove-attacker', { attackerId });
  return response.data;
};

export const reportToSwat = async (targetUserId, targetUsername = null) => {
  const payload = targetUserId ? { targetUserId } : { targetUsername };
  const response = await api.post('/swat/report', payload);
  return response.data;
};

export const getSwatStatus = async () => {
  const response = await api.get('/swat/status');
  return response.data;
};

export const getLawyerPricing = async () => {
  const response = await api.get('/swat/lawyer-pricing');
  return response.data;
};

export const hireLawyer = async (reductionHours) => {
  const response = await api.post('/swat/hire-lawyer', { reductionHours });
  return response.data;
};

// Drone System API
export const getAvailableDronePurchases = async () => {
  const response = await api.get('/drone/available-purchases');
  return response.data;
};

export const purchaseScoutDrone = async () => {
  const response = await api.post('/drone/purchase/scout-drone');
  return response.data;
};

export const purchaseAssaultDrone = async () => {
  const response = await api.post('/drone/purchase/assault-drone');
  return response.data;
};

export const purchaseNukeDrone = async () => {
  const response = await api.post('/drone/purchase/nuke-drone');
  return response.data;
};

export const purchaseMaritimeScoutDrone = async () => {
  const response = await api.post('/drone/purchase/maritime-scout-drone');
  return response.data;
};

export const checkDronePurchaseLimit = async () => {
  const response = await api.get('/drone/purchase-limit');
  return response.data;
};

export const getUserDrones = async () => {
  const response = await api.get('/drone/user-drones');
  return response.data;
};

export const deleteDrone = async (droneId) => {
  const response = await api.delete(`/drone/delete/${droneId}`);
  return response.data;
};

// === SATELLITE API FUNCTIONS ===

// Deploy satellite to ocean location
export const deploySatellite = async (lat, lng) => {
  const response = await api.post('/satellite/deploy', { lat, lng });
  return response.data;
};

// Get user's satellites
export const getUserSatellites = async () => {
  const response = await api.get('/satellite/user');
  return response.data;
};

// Get satellites in radius
export const getSatellitesInRadius = async (lat, lng, radius = 50000000) => {
  const response = await api.get(`/satellite/in-radius?lat=${lat}&lng=${lng}&radius=${radius}`);
  return response.data;
};

// Perform manual ocean scan
export const performOceanScan = async (satelliteId) => {
  const response = await api.post(`/satellite/${satelliteId}/scan`);
  return response.data;
};

// Get latest scan results for MDSS tab
export const getLatestScanResults = async () => {
  const response = await api.get('/satellite/scan-results');
  return response.data;
};

// Move satellite to new location
export const moveSatellite = async (satelliteId, lat, lng) => {
  const response = await api.put(`/satellite/${satelliteId}/move`, { lat, lng });
  return response.data;
};

// Delete/remove satellite
export const deleteSatellite = async (satelliteId) => {
  const response = await api.delete(`/satellite/${satelliteId}`);
  return response.data;
};

// Export the api instance as default for compatibility
export default api;